import { memo, useMemo } from 'react';

import { AvatarGroup } from '@mui/material';

import type { LeadsListItemFragment } from '@graphql/__generated__/graphql';
import { UserInfo } from '@modules/_shared/components/data-grid/UserInfo';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { useLeadsFilters } from '@modules/leads/components/context/leadsFiltersContext';
import { sortBySourceThenCreatedAt } from '@modules/leads/utils/sort';

type LeadAgents = LeadsListItemFragment['lead_agents'];
type LeadAgentCellProps = {
  leadAgents: LeadAgents;
};

export const LeadAgentCell = memo(({ leadAgents }: LeadAgentCellProps) => {
  const leadFilters = useLeadsFilters();

  const sortedLeadAgents = useMemo(() => {
    // We want to display first the agents that are filtered and then the others sorted by source and created at
    return [...leadAgents].sort((la1, la2) => {
      const firstWeight = leadFilters.agentIds.includes(la1.user_id) ? 1 : 0;
      const secondWeight = leadFilters.agentIds.includes(la2.user_id) ? 1 : 0;

      return secondWeight - firstWeight || sortBySourceThenCreatedAt(la1, la2);
    });
  }, [leadAgents, leadFilters]);

  if (sortedLeadAgents.length === 0) {
    return null;
  }

  if (sortedLeadAgents.length === 1) {
    return <UserInfo user={sortedLeadAgents[0].user} />;
  }

  return (
    <AvatarGroup max={3}>
      {sortedLeadAgents.map(agent => (
        <UserAvatar key={agent.id} user={agent.user} />
      ))}
    </AvatarGroup>
  );
});
